import { ChangeDetectionStrategy, Component, computed, inject, input, model, OnInit, signal } from "@angular/core";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { LineItemAdjData } from "../../../../pojo/Pricing";
import { Bind } from "../../../../decorators/bind.decorator";
import { Table } from "../../../s25-table/Table";
import { PricingTableAmountFormatComponent } from "./pricing.table.amount.format.component";
import { MovedItemData } from "../s25-pricing-orgs/s25.pricing.orgs.component";
import { S25PricingStore } from "../../s25.pricing.store";
import { GenericTableRenderHTMLComponent } from "../../../s25-table/generics/generic.table.render.html.component";

@TypeManagerDecorator("allocate-line-item-adj")
@Component({
    selector: "allocate-line-item-adj",
    template: `
        <p class="ngFinePrint">
            Line item adjustments have been added to
            @if (itemName()) {
                @if (isOccurrence()) {
                    the following items associated with the occurrence on <b>{{ itemName() }}</b
                    >.
                } @else {
                    <b>{{ itemName() }}</b
                    >.
                }
            } @else {
                the following line items.
            }
            These adjustments can be updated to account for the item's transfer between
            <b>{{ movedItem().fromNonInvoiced ? "Non-Invoiced Billing" : oldInvoiceName() }}</b> and
            <b>{{ newInvoiceName() }}</b
            >.
        </p>
        @if (movedItem().eventTypeMessage) {
            <p class="cn-alert cn-alert--warning">
                Event type line items, if any, do not appear below because they are not included in&nbsp;
                <b>{{ newInvoiceName() }}</b
                >.
            </p>
        }
        <s25-ng-table [dataSource]="tableConfig"></s25-ng-table>
    `,
    styles: `
        :host {
            .cn-alert.cn-alert--warning {
                font-size: 90%;
                margin-block: 5px;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class AllocateLineItemAdjComponent implements OnInit {
    lineItemAdj = model<LineItemAdjData[]>([]);
    movedItem = input<MovedItemData>(null);

    pricingStore = inject(S25PricingStore);

    itemName = computed(() => this.movedItem().itemData?.occurrence ?? this.movedItem().itemData?.bill_item_name);
    isOccurrence = computed(() => !!this.movedItem().itemData?.occurrence);
    tableConfig: Table.DataSource;
    newInvoice = computed(() =>
        this.pricingStore.invoiceSummaryData().find((invoice) => invoice.id === this.movedItem().newInvoiceId),
    );
    newInvoiceName = computed(() => this.newInvoice()?.name ?? this.movedItem()?.newInvoiceName ?? "New Invoice");
    oldInvoiceName = computed(() => this.lineItemAdj()?.[0]?.invoiceName);

    ngOnInit() {
        this.initTableConfig();
    }

    initTableConfig() {
        this.tableConfig = {
            type: "unpaginated",
            columns: [
                {
                    id: "name",
                    header: "Line Item",
                },
                ...(this.movedItem()?.fromNonInvoiced
                    ? []
                    : [
                          {
                              id: "oldInvoice",
                              header: `Adjustment for ${this.oldInvoiceName()}${this.movedItem()?.isLastOccurrence ? " (No occurrences remaining)" : ""}`,
                          },
                      ]),
                {
                    id: "newInvoice",
                    header: `Adjustment for ${this.newInvoiceName()}`,
                },
            ],
            dataSource: () => Promise.resolve({ rows: this.lineItemAdj().map(this.mapToRows) }),
        };
    }

    onUpdate(item: LineItemAdjData, index: number) {
        this.lineItemAdj.update((adjItems) => {
            const updatedItems = [...adjItems],
                targetItem = updatedItems[index];
            if (targetItem) {
                updatedItems[index] = {
                    ...targetItem,
                    newAdjustments: [
                        ...targetItem.newAdjustments.filter((adj) => adj.billId !== item.billId),

                        {
                            ...item,
                            chargeToId: targetItem.chargeToId,
                            itemId: targetItem.itemId,
                            itemType: targetItem.itemType,
                            profileId: targetItem.profileId,
                        },
                    ],
                };
            }
            return updatedItems;
        });
    }

    @Bind
    mapToRows(item: LineItemAdjData, index: number) {
        const { itemName, itemId, itemType } = item,
            match = this.newInvoice()?.lineItemAdjustments.find(
                (lineItem) => lineItem.itemId === item.itemId && lineItem.itemType === item.itemType,
            ),
            oldInvoiceUpdateData = signal<LineItemAdjData>(item),
            itemWillBeRemoved =
                this.movedItem()?.itemIdsToBeRemoved &&
                this.movedItem().itemIdsToBeRemoved.has(`${item.itemType}-${item.itemId}`),
            itemNameText = itemWillBeRemoved
                ? `<p>${itemName}</p><p class="ngFinePrint">Adjustment will be removed from <b>${this.oldInvoiceName()}</b> after moving occurrence, as no remaining occurrence(s) include this line item.</p>`
                : itemName;
        return {
            id: `${itemId}${itemType}`,
            name: itemName,
            cells: {
                name: {
                    component: GenericTableRenderHTMLComponent,
                    inputs: {
                        isHtml: itemWillBeRemoved,
                        text: itemNameText,
                    },
                },
                ...(!this.movedItem()?.fromNonInvoiced && {
                    oldInvoice: {
                        component: PricingTableAmountFormatComponent,
                        inputs: {
                            itemData: item,
                            isNewInvoice: false,
                            oldInvoiceUpdateData: oldInvoiceUpdateData,
                            disabled: this.movedItem()?.isLastOccurrence || itemWillBeRemoved,
                        },
                        outputs: {
                            updateAdjustment: (updatedItem: LineItemAdjData) => this.onUpdate(updatedItem, index),
                        },
                    },
                }),
                newInvoice: {
                    component: PricingTableAmountFormatComponent,
                    inputs: {
                        itemData: item,
                        isNewInvoice: true,
                        itemDataNewInvoice: match ?? {
                            invoiceName: this.newInvoiceName(),
                            billId: this.newInvoice()?.id,
                            itemId,
                            itemType,
                        },
                        oldInvoiceUpdateData: oldInvoiceUpdateData,
                        hideApplyAdjButton: this.movedItem()?.fromNonInvoiced,
                    },
                    outputs: {
                        updateAdjustment: (updatedItem: LineItemAdjData) => this.onUpdate(updatedItem, index),
                    },
                },
            },
        };
    }
}
